package no.vareflow.app.domain

/** Why stock was lost (svinn). */
enum class SvinnReason(private val nb: String, private val en: String) {
    KNUST("Knust", "Broken"),
    UTGATT("Utgått", "Expired"),
    SOL("Søl", "Spilled"),
    PERSONAL("Personal", "Staff"),
    ANNET("Annet", "Other");

    val label: String get() = t(nb, en)
    fun labelIn(language: AppLanguage): String = tr(language, nb, en)

    companion object {
        fun parse(value: String?): SvinnReason = entries.firstOrNull { it.name == value } ?: ANNET
    }
}

/**
 * Registered svinn: stock that was lost (broken, expired, spilled, staff use …). Not a stock
 * count – it is a movement, shown per month in Mer → Svinn, Analyse and the report. Price and
 * names are copied at registration, so later product changes do not rewrite history.
 */
data class SvinnRecord(
    val id: String = randomId(),
    val yearMonth: String,
    val sessionId: String? = null,
    val productId: String,
    val productName: String,
    val category: String,
    val unit: String,
    val pricePerUnit: Double,
    val quantity: Double,
    val reason: SvinnReason,
    val note: String = "",
    val userId: String,
    val userName: String,
    val createdAt: Long = nowMillis(),
    val updatedAt: Long = createdAt
) {
    val value: Double get() = quantity * pricePerUnit
}

/** Svinn of one month: total value, per hovedvare (in [categoryOrder]) and per reason. */
data class SvinnSummary(
    val count: Int,
    val value: Double,
    val byCategory: List<Pair<String, Double>>,
    val byReason: List<Pair<SvinnReason, Double>>
)

fun summarizeSvinn(records: List<SvinnRecord>, categoryOrder: List<String>): SvinnSummary {
    val byCategory = records.groupBy { it.category }.mapValues { (_, list) -> list.sumOf { it.value } }
        .toList().sortedBy { (c, _) -> categoryOrder.indexOf(c).let { if (it < 0) Int.MAX_VALUE else it } }
    val byReason = SvinnReason.entries.mapNotNull { reason ->
        records.filter { it.reason == reason }.takeIf { it.isNotEmpty() }?.let { reason to it.sumOf { r -> r.value } }
    }
    return SvinnSummary(records.size, records.sumOf { it.value }, byCategory, byReason)
}
